import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceScoring = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');

  const complianceScores = {
    overall: {
      current: 87,
      previous: 82,
      trend: 'up',
      status: 'good'
    },
    categories: [
      {
        id: 1,
        name: 'EUA Trading Compliance',
        score: 92,
        previousScore: 89,
        trend: 'up',
        status: 'excellent',
        description: 'European Union Allowances trading regulatory compliance',
        lastUpdated: '2025-09-21T09:00:00Z',
        issues: 1,
        resolved: 8
      },
      {
        id: 2,
        name: 'Oil Futures Compliance',
        score: 85,
        previousScore: 88,
        trend: 'down',
        status: 'good',
        description: 'Oil futures trading and position reporting compliance',
        lastUpdated: '2025-09-21T08:30:00Z',
        issues: 3,
        resolved: 12
      },
      {
        id: 3,
        name: 'Risk Management',
        score: 78,
        previousScore: 75,
        trend: 'up',
        status: 'fair',
        description: 'Risk management policies and position limits compliance',
        lastUpdated: '2025-09-21T07:45:00Z',
        issues: 5,
        resolved: 6
      },
      {
        id: 4,
        name: 'Reporting & Documentation',
        score: 94,
        previousScore: 91,
        trend: 'up',
        status: 'excellent',
        description: 'Regulatory reporting and documentation requirements',
        lastUpdated: '2025-09-21T09:15:00Z',
        issues: 0,
        resolved: 15
      },
      {
        id: 5,
        name: 'AML/KYC Compliance',
        score: 88,
        previousScore: 86,
        trend: 'up',
        status: 'good',
        description: 'Anti-money laundering and know your customer compliance',
        lastUpdated: '2025-09-21T08:00:00Z',
        issues: 2,
        resolved: 10
      }
    ]
  };

  const riskIndicators = [
    {
      id: 1,
      type: 'Position Concentration',
      level: 'medium',
      description: 'Oil futures position concentration at 78% of limit',
      impact: 'medium',
      recommendation: 'Consider diversifying positions across different contract months'
    },
    {
      id: 2,
      type: 'Reporting Deadline',
      level: 'high',
      description: 'MiFID II reporting due in 1 day',
      impact: 'high',
      recommendation: 'Complete transaction reporting immediately to avoid penalties'
    },
    {
      id: 3,
      type: 'Documentation Gap',
      level: 'low',
      description: 'Missing verification documents for 2 EUA transactions',
      impact: 'low',
      recommendation: 'Upload required documentation within 5 business days'
    }
  ];

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 80) return 'text-accent';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getScoreBackground = (score) => {
    if (score >= 90) return 'bg-success/10';
    if (score >= 80) return 'bg-accent/10';
    if (score >= 70) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-success bg-success/10 border-success/20';
      case 'good': return 'text-accent bg-accent/10 border-accent/20';
      case 'fair': return 'text-warning bg-warning/10 border-warning/20';
      case 'poor': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low': return 'text-success bg-success/10 border-success/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'high': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 'TrendingUp' : trend === 'down' ? 'TrendingDown' : 'Minus';
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-success' : trend === 'down' ? 'text-error' : 'text-muted-foreground';
  };

  const timeframeOptions = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  return (
    <div className="space-y-6">
      {/* Overall Compliance Score */}
      <div className="bg-card rounded-lg border border-border shadow-subtle">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Award" size={18} className="text-success" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Compliance Scoring</h2>
                <p className="text-sm text-muted-foreground">Overall compliance health and performance metrics</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {timeframeOptions?.map(option => (
                <button
                  key={option?.value}
                  onClick={() => setSelectedTimeframe(option?.value)}
                  className={`
                    px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200
                    ${selectedTimeframe === option?.value
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  {option?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Overall Score Display */}
          <div className="flex items-center justify-center">
            <div className="relative w-32 h-32">
              <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-muted"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 50}`}
                  strokeDashoffset={`${2 * Math.PI * 50 * (1 - complianceScores?.overall?.current / 100)}`}
                  className={getScoreColor(complianceScores?.overall?.current)}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className={`text-2xl font-bold ${getScoreColor(complianceScores?.overall?.current)}`}>
                    {complianceScores?.overall?.current}
                  </div>
                  <div className="text-xs text-muted-foreground">Overall Score</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Icon 
                name={getTrendIcon(complianceScores?.overall?.trend)} 
                size={16} 
                className={getTrendColor(complianceScores?.overall?.trend)} 
              />
              <span className="text-sm text-muted-foreground">
                {complianceScores?.overall?.trend === 'up' ? '+' : '-'}
                {Math.abs(complianceScores?.overall?.current - complianceScores?.overall?.previous)} from last period
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Category Scores */}
      <div className="bg-card rounded-lg border border-border shadow-subtle">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground mb-2">Compliance Categories</h3>
          <p className="text-sm text-muted-foreground">Detailed breakdown by compliance area</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {complianceScores?.categories?.map(category => (
              <div key={category?.id} className="border border-border rounded-lg p-4 hover:shadow-subtle transition-shadow duration-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-foreground">{category?.name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(category?.status)}`}>
                        {category?.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{category?.description}</p>
                  </div>
                  <div className={`text-2xl font-bold ${getScoreColor(category?.score)} ml-4`}>
                    {category?.score}
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-1">
                    <Icon 
                      name={getTrendIcon(category?.trend)} 
                      size={14} 
                      className={getTrendColor(category?.trend)} 
                    />
                    <span className="text-xs text-muted-foreground">
                      {category?.trend === 'up' ? '+' : '-'}
                      {Math.abs(category?.score - category?.previousScore)} from last period
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Icon name="AlertTriangle" size={12} className="text-error" />
                      {category?.issues} issues
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="CheckCircle" size={12} className="text-success" />
                      {category?.resolved} resolved
                    </span>
                  </div>
                  <span>Updated {new Date(category.lastUpdated)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${getScoreBackground(category?.score)} ${getScoreColor(category?.score)?.replace('text-', 'bg-')}`}
                    style={{ width: `${category?.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Risk Indicators */}
      <div className="bg-card rounded-lg border border-border shadow-subtle">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-error/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={18} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Risk Indicators</h3>
                <p className="text-sm text-muted-foreground">Active compliance risks and recommendations</p>
              </div>
            </div>
            <Button variant="outline" iconName="Settings" iconPosition="left" size="sm">
              Configure Alerts
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {riskIndicators?.map(indicator => (
              <div key={indicator?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-foreground">{indicator?.type}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskLevelColor(indicator?.level)}`}>
                        {indicator?.level?.toUpperCase()} RISK
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{indicator?.description}</p>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-1">Recommendation</h5>
                          <p className="text-sm text-muted-foreground">{indicator?.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button variant="ghost" size="sm" iconName="Eye" />
                    <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {riskIndicators?.length === 0 && (
            <div className="text-center py-8">
              <Icon name="Shield" size={48} className="text-success mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No active risks</h3>
              <p className="text-muted-foreground">All compliance indicators are within acceptable ranges.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceScoring;